import { useState, useEffect } from 'react';
import { UserGroupIcon, GlobeAltIcon, BuildingOffice2Icon, SparklesIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import PartnerForm from '../../components/PartnerForm';
import { useSponsorshipTiers } from '../../hooks/useSponsorshipTiers';

interface Partner {
  id: string;
  company_name: string;
  name?: string;
  description?: string;
  logo_url?: string;
  website?: string;
  tier: string;
  stats?: string[];
  image_url?: string;
  is_active: boolean;
}

const TIER_ORDER = [
  'Tournament Host', 'Diamond', 'Title Sponsor', 'Platinum', 'Gold',
  'Official Airline', 'Official Bank', 'Official Beverage', 'Official Telecom Partner',
  'Official Automotive Brand', 'Official Insurance Partner', 'Official Real Estate Partner',
  'Official Media Partner', 'Official Logistics Partner', 'Official Construction Partner',
  'Official Apparel Brand', 'Silver', 'Bronze', 'Other'
];

const PREMIUM_TIERS = ['Tournament Host', 'Diamond', 'Title Sponsor', 'Platinum'];

function tierBadgeStyle(tier: string) {
  if (tier === 'Tournament Host') return 'bg-golf-green text-white';
  if (tier === 'Diamond' || tier === 'Title Sponsor') return 'bg-amber-100 text-amber-700';
  if (tier === 'Platinum') return 'bg-slate-100 text-slate-600';
  if (tier === 'Gold' || tier.startsWith('Official')) return 'bg-yellow-50 text-yellow-700';
  if (tier === 'Silver') return 'bg-gray-100 text-gray-500';
  if (tier === 'Bronze') return 'bg-orange-50 text-orange-600';
  return 'bg-gray-100 text-gray-500';
}

const benefits = [
  { icon: GlobeAltIcon, title: 'Global Exposure', desc: 'Reach millions through international marketing and digital channels' },
  { icon: UserGroupIcon, title: 'VIP Access', desc: 'Exclusive networking with industry leaders and pro golfers' },
  { icon: BuildingOffice2Icon, title: 'Brand Integration', desc: 'Custom activation opportunities across the tournament' },
];


type Tab = 'partners' | 'benefits' | 'tiers';

export default function MobileSponsors() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('partners');
  const [formOpen, setFormOpen] = useState(false);
  const { tiers: dbTiers, loading: tiersLoading } = useSponsorshipTiers();
  const displayTiers = dbTiers.filter(t => !t.is_category_tier && t.name !== 'Tournament Host');

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data } = await supabase
        .from('partners')
        .select('*')
        .eq('status', 'active')
        .lte('start_date', today)
        .gte('end_date', today)
        .order('tier');
      if (data) setPartners(data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const grouped: Record<string, Partner[]> = {};
  for (const p of partners) {
    const tier = p.tier || 'Other';
    if (!grouped[tier]) grouped[tier] = [];
    grouped[tier].push(p);
  }
  const sortedTiers = TIER_ORDER.filter(t => grouped[t]?.length);
  const unsortedTiers = Object.keys(grouped).filter(t => !TIER_ORDER.includes(t));
  const allTiers = [...sortedTiers, ...unsortedTiers];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'partners', label: 'Partners' },
    { id: 'benefits', label: 'Benefits' },
    { id: 'tiers', label: 'Tiers' },
  ];

  return (
    <div className="pb-10">
      {/* Header */}
      <div
        className="relative h-32 bg-cover bg-center overflow-hidden"
        style={{ backgroundImage: `url(https://images.pexels.com/photos/1325681/pexels-photo-1325681.jpeg?auto=compress&w=800)` }}
      >
        <div className="absolute inset-0 bg-golf-gray/80" />
        <div className="relative z-10 h-full flex items-end justify-between p-4">
          <div>
            <p className="text-golf-orange text-xs font-semibold uppercase tracking-wider mb-1">Ibom Open</p>
            <h1 className="text-white font-display font-bold text-xl">Partners & Sponsors</h1>
          </div>
          <button
            onClick={() => setFormOpen(true)}
            className="bg-golf-orange text-white text-xs font-semibold px-3 py-2 rounded-xl active:opacity-80"
          >
            Become a Partner
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 bg-white sticky top-14 z-10">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 text-xs font-semibold transition-colors border-b-2 ${
              activeTab === tab.id ? 'text-golf-orange border-golf-orange' : 'text-gray-400 border-transparent'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Partners Tab */}
      {activeTab === 'partners' && (
        <div className="px-4 pt-4">
          {!partners.length ? (
            <div className="text-center py-16">
              <UserGroupIcon className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No partners listed yet.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {allTiers.map(tier => (
                <div key={tier}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${tierBadgeStyle(tier)}`}>
                      {tier}
                    </span>
                  </div>

                  <div className="flex flex-col gap-3">
                    {grouped[tier].map(partner => {
                      const isPremium = PREMIUM_TIERS.includes(tier);
                      const name = partner.company_name || partner.name || '';
                      return (
                        <div
                          key={partner.id}
                          className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${isPremium ? 'border-golf-orange/20' : 'border-gray-100'}`}
                        >
                          {isPremium && partner.image_url && (
                            <div className="h-32 overflow-hidden">
                              <img
                                src={partner.image_url}
                                alt={name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                          )}
                          <div className="p-4 flex items-center gap-4">
                            <div className="w-16 h-12 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0 border border-gray-100 overflow-hidden">
                              {partner.logo_url ? (
                                <img
                                  src={partner.logo_url}
                                  alt={name}
                                  className="max-h-full max-w-full object-contain p-1"
                                  loading="lazy"
                                />
                              ) : (
                                <span className="text-[10px] font-medium text-gray-400 text-center leading-tight px-1">{name}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-800 text-sm">{name}</p>
                              {partner.description && (
                                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{partner.description}</p>
                              )}
                              {partner.stats && partner.stats.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {partner.stats.slice(0, 2).map((stat, i) => (
                                    <span key={i} className="text-[10px] bg-golf-sand/60 text-golf-gray px-2 py-0.5 rounded-full">{stat}</span>
                                  ))}
                                </div>
                              )}
                              {partner.website && partner.website !== '#' && (
                                <a
                                  href={partner.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-golf-orange font-medium mt-1.5 inline-flex items-center gap-1"
                                >
                                  <GlobeAltIcon className="w-3 h-3" />
                                  Visit Website
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Benefits Tab */}
      {activeTab === 'benefits' && (
        <div className="px-4 pt-4 flex flex-col gap-4">
          {benefits.map((b, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex gap-4">
              <div className="w-11 h-11 rounded-xl bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
                <b.icon className="w-6 h-6 text-golf-orange" />
              </div>
              <div>
                <p className="font-semibold text-golf-gray text-sm mb-1">{b.title}</p>
                <p className="text-xs text-gray-500 leading-relaxed">{b.desc}</p>
              </div>
            </div>
          ))}

          <div className="bg-golf-green rounded-2xl p-4 mt-2">
            <p className="text-golf-sand text-xs mb-3 leading-relaxed">
              Join an exclusive community of brands shaping the future of golf in Africa
            </p>
            <button
              onClick={() => setFormOpen(true)}
              className="bg-golf-orange text-white text-sm font-semibold px-4 py-2.5 rounded-xl w-full active:opacity-80"
            >
              Become a Partner
            </button>
          </div>
        </div>
      )}

      {/* Tiers Tab */}
      {activeTab === 'tiers' && (
        <div className="px-4 pt-4 flex flex-col gap-4">
          {tiersLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-golf-orange border-t-transparent" />
            </div>
          ) : (
            displayTiers.map((tier) => (
              <div key={tier.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <SparklesIcon className="w-4 h-4 text-golf-orange" />
                    <h3 className="font-display font-bold text-golf-gray text-base">{tier.name}</h3>
                  </div>
                  <span className="font-display font-bold text-golf-orange text-base">{tier.price_label}</span>
                </div>
                <ul className="space-y-1.5 mb-4">
                  {tier.features.map((f, fi) => (
                    <li key={fi} className="flex items-center gap-2 text-xs text-gray-600">
                      <svg className="w-3.5 h-3.5 text-golf-orange flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setFormOpen(true)}
                  className="w-full bg-golf-orange text-white text-xs font-semibold py-2.5 rounded-xl active:opacity-80"
                >
                  Contact Us
                </button>
              </div>
            ))
          )}
        </div>
      )}

      <PartnerForm isOpen={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}
